import { useMemo } from 'react';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { useSiteStore } from '@/store/site';
import { useBetslipStore } from '@/store/betslip';
import { useGoldenDuelSetting } from '../query/golden-duel';
import { useAccountInfo } from '../query/account';

export const TICKET_STATES = {
	NO_STAKE: 'NO_STAKE',
	AWAY_COUNT: 'AWAY_COUNT',
	REACHED: 'REACHED',
	NO_AMOUNT: 'NO_AMOUNT'
} as const;

export interface TicketEligibilityInfo {
	isEligible: boolean;
	targetAmount: number;
	currentStake: number;
	awayCount: number;
	ticketState: typeof TICKET_STATES[keyof typeof TICKET_STATES];
	currencyName: string;
}

const getTicketState = (currentStake: number, awayCount: number): typeof TICKET_STATES[keyof typeof TICKET_STATES] => {
	if (!currentStake) return TICKET_STATES.NO_STAKE;
	if (awayCount > 0) return TICKET_STATES.AWAY_COUNT;
	if (awayCount === 0) return TICKET_STATES.REACHED;
	return TICKET_STATES.NO_AMOUNT;
};

export const useGoldenDuelTicketEligibility = (ticket: {
	LeagueId: number;
	stake: number;
}): TicketEligibilityInfo => {
	const { data: goldenDuelSetting } = useGoldenDuelSetting();
	const { user } = useAccountInfo();
	const { currencyName } = useBetslipContext();
	const [isParlayMode] = useSiteStore(state => [state.isParlayMode]);
	const [betslipTab, useSabaCoin] = useBetslipStore(state => [state.betslipTab, state.useSabaCoin]);
	const isParlayTab = betslipTab === 'parlay';

	const isEligibleLeague = useMemo(() => {
		if (!goldenDuelSetting?.LeagueIds) return false;
		return goldenDuelSetting.LeagueIds.includes(ticket.LeagueId);
	}, [goldenDuelSetting?.LeagueIds, ticket.LeagueId]);
  
	const currencySetting = useMemo(() => {
		if (!goldenDuelSetting || !user || !isEligibleLeague) return null;
    
		const userCurrencyId = user.CurrId;
		return goldenDuelSetting.CurrencySettings.find(
			setting => setting.CurrencyId === userCurrencyId
		);
	}, [goldenDuelSetting, user, isEligibleLeague]);

	const targetAmount = useMemo(() => 
		currencySetting?.TargetAmount ?? 0
	, [currencySetting]);

	const awayCount = useMemo(() => {
		if (!currencySetting) return 0;
    
		const currentStake = ticket.stake || 0;
		const awayCount = Math.max(0, targetAmount - currentStake);
		return Math.ceil(awayCount);
	}, [currencySetting, targetAmount, ticket.stake]);

	const isEligible = useMemo(() => {
		if (!isEligibleLeague || isParlayMode || isParlayTab || useSabaCoin) return false;
		return true;
	}, [isEligibleLeague, isParlayMode, isParlayTab, useSabaCoin]);

	const ticketState = getTicketState(ticket.stake, awayCount);

	return {
		isEligible,
		targetAmount,
		currentStake: ticket.stake || 0,
		awayCount,
		currencyName,
		ticketState
	};
}; 